import { useEffect, useMemo, useRef, useState } from 'react'
import type { HomeworkComment } from '../../services/homeworkApi'

type Props = {
  comment: HomeworkComment
  replies: HomeworkComment[]
  open: boolean
  anchorRect: DOMRect | null
  canReply?: boolean
  draft?: string
  onDraftChange?: (value: string) => void
  onClose: () => void
  onReply?: (body: string) => Promise<void> | void
  onKeepOpen?: () => void
  /** Khi true, mouse leave không đóng (đang soạn reply) */
  pinned?: boolean
  onPinnedChange?: (pinned: boolean) => void
}

function sourceLabel(c: HomeworkComment) {
  if (c.source === 'ai') return 'AI'
  if (c.source === 'student') return c.authorName || 'Học viên'
  return c.authorName || 'Admin'
}

export default function HomeworkCommentHoverCard({
  comment,
  replies,
  open,
  anchorRect,
  canReply = true,
  draft: controlledDraft,
  onDraftChange,
  onClose,
  onReply,
  onKeepOpen,
  pinned,
  onPinnedChange,
}: Props) {
  const [localDraft, setLocalDraft] = useState('')
  const [sending, setSending] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const draft = controlledDraft !== undefined ? controlledDraft : localDraft

  const setDraft = (value: string) => {
    if (onDraftChange) onDraftChange(value)
    else setLocalDraft(value)
    if (value.trim()) onPinnedChange?.(true)
  }

  useEffect(() => {
    if (controlledDraft === undefined && open) {
      // keep local draft across reopen for same comment
    }
  }, [open, comment.id, controlledDraft])

  const style = useMemo(() => {
    if (!anchorRect) return { top: 0, left: 0 }
    const pad = 8
    const width = 320
    let left = anchorRect.left + anchorRect.width / 2 - width / 2
    left = Math.max(pad, Math.min(left, window.innerWidth - width - pad))
    let top = anchorRect.bottom + 6
    if (top + 280 > window.innerHeight) {
      top = Math.max(pad, anchorRect.top - 6 - 260)
    }
    return { top, left, width }
  }, [anchorRect])

  if (!open || !anchorRect) return null

  const submit = async () => {
    const body = draft.trim()
    if (!body || !onReply) return
    try {
      setSending(true)
      await onReply(body)
      setDraft('')
      onPinnedChange?.(false)
    } finally {
      setSending(false)
    }
  }

  const handleMouseLeave = () => {
    const active = document.activeElement
    const focusedInside = Boolean(active && panelRef.current?.contains(active))
    if (pinned || focusedInside || draft.trim()) {
      onKeepOpen?.()
      return
    }
    onClose()
  }

  return (
    <div
      ref={panelRef}
      className="fixed z-[80] rounded-xl border border-m2-outline-variant bg-white shadow-xl p-3 text-sm"
      style={style}
      onMouseEnter={() => onKeepOpen?.()}
      onMouseLeave={handleMouseLeave}
    >
      <div className="text-[11px] text-m2-on-surface-variant mb-1 flex justify-between gap-2">
        <span>
          {sourceLabel(comment)} · {comment.status}
          {comment.visibility === 'pending_approval' ? ' · chờ duyệt' : ''}
        </span>
        <button
          type="button"
          className="text-xs text-m2-on-surface-variant"
          onClick={() => {
            onPinnedChange?.(false)
            onClose()
          }}
        >
          ✕
        </button>
      </div>
      <p className="text-m2-on-surface whitespace-pre-wrap mb-2 max-h-28 overflow-auto">{comment.body}</p>

      {replies.length > 0 && (
        <div className="border-t border-m2-outline-variant/60 pt-2 mb-2 space-y-1.5 max-h-32 overflow-auto">
          <p className="text-[11px] font-semibold text-m2-on-surface-variant">
            Replies ({replies.length})
          </p>
          {replies.map((r) => (
            <div key={r.id} className="rounded-md bg-m2-surface-low px-2 py-1.5 text-xs">
              <span className="font-medium">{sourceLabel(r)}: </span>
              <span className="whitespace-pre-wrap">{r.body}</span>
            </div>
          ))}
        </div>
      )}

      {canReply && onReply && (
        <div className="border-t border-m2-outline-variant/60 pt-2">
          <textarea
            className="w-full border border-m2-outline-variant rounded-md px-2 py-1.5 text-xs"
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onFocus={() => {
              onPinnedChange?.(true)
              onKeepOpen?.()
            }}
            placeholder="Nhập reply..."
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                void submit()
              }
            }}
          />
          <div className="flex justify-end mt-1.5 gap-2">
            {draft.trim() && (
              <button
                type="button"
                className="px-2.5 py-1 text-xs rounded-md border text-m2-on-surface-variant"
                onClick={() => {
                  setDraft('')
                  onPinnedChange?.(false)
                }}
              >
                Xóa nháp
              </button>
            )}
            <button
              type="button"
              disabled={sending || !draft.trim()}
              onClick={() => void submit()}
              className="px-2.5 py-1 text-xs rounded-md bg-m2-primary text-white disabled:opacity-50"
            >
              {sending ? 'Đang gửi…' : 'Gửi reply'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
